import { FC, useMemo } from 'react';
import { ResponsiveHeatMap } from '@nivo/heatmap';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useNivoChartTheme } from '@/hooks/useNivoChartTheme';
import { ReportComponent } from '@/types';
import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import {
    formatCompactChartValue,
    getChartFormatting,
    humanizeChartLabel,
    truncateChartLabel,
} from '../chartPresentation';
import CustomNivoChartTooltip from '../CustomNivoChartTooltip';
import { useNivoReportData } from '../useNivoReportData';
import { useNivoReportFormatting } from '../useNivoReportFormatting';

const data = [
    {
        id: 'Japan',
        data: [
            {
                x: 'Train',
                y: 45121,
            },
            {
                x: 'Subway',
                y: -66759,
            },
            {
                x: 'Bus',
                y: 24077,
            },
            {
                x: 'Car',
                y: -22038,
            },
            {
                x: 'Boat',
                y: 51197,
            },
            {
                x: 'Moto',
                y: -4428,
            },
            {
                x: 'Moped',
                y: 67579,
            },
            {
                x: 'Bicycle',
                y: 23981,
            },
            {
                x: 'Others',
                y: 56978,
            },
        ],
    },
    {
        id: 'France',
        data: [
            {
                x: 'Train',
                y: 95468,
            },
            {
                x: 'Subway',
                y: -95026,
            },
            {
                x: 'Bus',
                y: -71758,
            },
            {
                x: 'Car',
                y: 36129,
            },
            {
                x: 'Boat',
                y: -71863,
            },
            {
                x: 'Moto',
                y: -23333,
            },
            {
                x: 'Moped',
                y: -79829,
            },
            {
                x: 'Bicycle',
                y: -48911,
            },
            {
                x: 'Others',
                y: 93831,
            },
        ],
    },
    {
        id: 'US',
        data: [
            {
                x: 'Train',
                y: 6390,
            },
            {
                x: 'Subway',
                y: 3335,
            },
            {
                x: 'Bus',
                y: -91058,
            },
            {
                x: 'Car',
                y: -18705,
            },
            {
                x: 'Boat',
                y: 48178,
            },
            {
                x: 'Moto',
                y: -42747,
            },
            {
                x: 'Moped',
                y: 78396,
            },
            {
                x: 'Bicycle',
                y: 90474,
            },
            {
                x: 'Others',
                y: -41120,
            },
        ],
    },
    {
        id: 'Germany',
        data: [
            {
                x: 'Train',
                y: -75009,
            },
            {
                x: 'Subway',
                y: 81671,
            },
            {
                x: 'Bus',
                y: -49660,
            },
            {
                x: 'Car',
                y: 79437,
            },
            {
                x: 'Boat',
                y: 40815,
            },
            {
                x: 'Moto',
                y: -930,
            },
            {
                x: 'Moped',
                y: -87578,
            },
            {
                x: 'Bicycle',
                y: 69699,
            },
            {
                x: 'Others',
                y: 63901,
            },
        ],
    },
    {
        id: 'Norway',
        data: [
            {
                x: 'Train',
                y: -72310,
            },
            {
                x: 'Subway',
                y: 3809,
            },
            {
                x: 'Bus',
                y: 10437,
            },
            {
                x: 'Car',
                y: -96985,
            },
            {
                x: 'Boat',
                y: -66518,
            },
            {
                x: 'Moto',
                y: -95999,
            },
            {
                x: 'Moped',
                y: -48221,
            },
            {
                x: 'Bicycle',
                y: 84894,
            },
            {
                x: 'Others',
                y: 83001,
            },
        ],
    },
    {
        id: 'Iceland',
        data: [
            {
                x: 'Train',
                y: 60151,
            },
            {
                x: 'Subway',
                y: 88684,
            },
            {
                x: 'Bus',
                y: 83855,
            },
            {
                x: 'Car',
                y: 42864,
            },
            {
                x: 'Boat',
                y: 15771,
            },
            {
                x: 'Moto',
                y: 70487,
            },
            {
                x: 'Moped',
                y: 83728,
            },
            {
                x: 'Bicycle',
                y: 62167,
            },
            {
                x: 'Others',
                y: 78964,
            },
        ],
    },
    {
        id: 'UK',
        data: [
            {
                x: 'Train',
                y: -95996,
            },
            {
                x: 'Subway',
                y: -28597,
            },
            {
                x: 'Bus',
                y: 77744,
            },
            {
                x: 'Car',
                y: -77665,
            },
            {
                x: 'Boat',
                y: -7708,
            },
            {
                x: 'Moto',
                y: 32313,
            },
            {
                x: 'Moped',
                y: 28342,
            },
            {
                x: 'Bicycle',
                y: 98558,
            },
            {
                x: 'Others',
                y: 14331,
            },
        ],
    },
    {
        id: 'Vietnam',
        data: [
            {
                x: 'Train',
                y: -82124,
            },
            {
                x: 'Subway',
                y: -76786,
            },
            {
                x: 'Bus',
                y: -56139,
            },
            {
                x: 'Car',
                y: -1970,
            },
            {
                x: 'Boat',
                y: 28952,
            },
            {
                x: 'Moto',
                y: 76586,
            },
            {
                x: 'Moped',
                y: 60544,
            },
            {
                x: 'Bicycle',
                y: -40376,
            },
            {
                x: 'Others',
                y: -69433,
            },
        ],
    },
];

interface HeatMapProps {
    component: ReportComponent;
    numerator: number;
    denominator: number;
}

const HeatMap: FC<HeatMapProps> = ({ component, numerator, denominator }): JSX.Element => {
    const { token } = useAntDesignTheme();

    const { nivoChartTheme } = useNivoChartTheme();
    const chartData = useNivoReportData(component, data);
    const { financialValueDisplay, formatMetricValue, formatDimensionValue, getDimensionLabel, getMetricLabel } =
        useNivoReportFormatting(component);
    const config = component.configuration as ReportComponentConfig;
    const formatting = getChartFormatting(config);
    const numericValues = useMemo(
        () =>
            chartData
                .flatMap((serie) => serie.data.map((point) => Number(point.y)))
                .filter((value) => Number.isFinite(value)),
        [chartData],
    );
    const minValue = numericValues.length ? Math.min(...numericValues) : 0;
    const maxValue = numericValues.length ? Math.max(...numericValues) : 1;
    const hasNegativeValues = minValue < 0;

    return (
        <div
            style={{
                aspectRatio: `${numerator}/${denominator}`,
                height: '100%',
                minHeight: 0,
                width: '100%',
            }}
        >
            <ResponsiveHeatMap
                data={chartData}
                theme={nivoChartTheme}
                margin={{ top: 56, right: 32, bottom: formatting.showLegend ? 72 : 40, left: 72 }}
                valueFormat={(value) => formatMetricValue(value)}
                xOuterPadding={0.1}
                yOuterPadding={0.1}
                xInnerPadding={0.075}
                yInnerPadding={0.075}
                borderRadius={2}
                borderWidth={1}
                borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                axisTop={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: -35,
                    format: (value) => truncateChartLabel(formatDimensionValue(value, 1), 14),
                    legend: formatting.showAxisLabels ? getDimensionLabel(1) : undefined,
                    legendOffset: 46,
                    legendPosition: 'middle',
                    truncateTickAt: 14,
                }}
                axisRight={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    format: (value) => truncateChartLabel(formatDimensionValue(value, 0), 16),
                    legend: formatting.showAxisLabels ? getDimensionLabel(0) : undefined,
                    legendPosition: 'middle',
                    legendOffset: 70,
                    truncateTickAt: 16,
                }}
                axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    format: (value) => truncateChartLabel(formatDimensionValue(value, 0), 16),
                    legend: formatting.showAxisLabels ? getDimensionLabel(0) : undefined,
                    legendPosition: 'middle',
                    legendOffset: -72,
                    truncateTickAt: 16,
                }}
                colors={
                    hasNegativeValues
                        ? {
                              type: 'diverging',
                              colors: [token.red6, token.colorBgContainer, token.blue6],
                              minValue,
                              maxValue,
                          }
                        : {
                              type: 'sequential',
                              colors: [token.blue1, token.blue7],
                              minValue: 0,
                              maxValue,
                          }
                }
                emptyColor={token.colorBorder}
                label={(cell) => formatCompactChartValue(cell.value, formatMetricValue, financialValueDisplay)}
                labelTextColor={token.colorTextLightSolid}
                tooltip={({ cell }) => (
                    <CustomNivoChartTooltip
                        title={`${humanizeChartLabel(cell.serieId)} / ${humanizeChartLabel(cell.data.x)}`}
                        entries={[
                            {
                                color: cell.color,
                                label: getMetricLabel() || 'Value',
                                value: cell.value,
                            },
                        ]}
                        formatValue={formatMetricValue}
                    />
                )}
                legends={
                    formatting.showLegend
                        ? [
                              {
                                  anchor: 'bottom',
                                  direction: 'row',
                                  length: 240,
                                  thickness: 8,
                                  tickFormat: (value: number) =>
                                      formatCompactChartValue(value, formatMetricValue, financialValueDisplay),
                                  tickPosition: 'after',
                                  tickSize: 3,
                                  tickSpacing: 4,
                                  translateX: 0,
                                  translateY: 48,
                              },
                          ]
                        : []
                }
            />
        </div>
    );
};

export default HeatMap;
